/**
 * Enrolments — every enrolment in the academy, filtered.
 *
 * NOT one course at a time, which is where this page started and why it was wrong. An
 * administrator's question is usually "who joined this week", "who is auditing rather than taking
 * this for credit", or "is this person actually enrolled" — none of which begin with picking a
 * course. So the page opens on the whole academy and the course becomes one filter among several.
 *
 * `academy/enrolments` is the right source for that: ONE tenant-scoped request rather than the
 * `ceil(courses / 25)` chunked calls to `/api/enrollment/v1/enrollments?course_ids=…` it replaces
 * (4 on this academy, ~200 at five thousand courses, because that endpoint's scope had to travel
 * in the query string as a list of course keys). It carries `created` and `is_active` — when
 * somebody enrolled, and whether they have since left — and now arrives with `course_title`,
 * `email` and `name` already joined server-side, so there is no catalogue lookup needed to show a
 * row; see `shared/lib/academy.ts::read_enrolments` for the join.
 *
 * Writing is still per course, because an enrolment belongs to one: the add/remove panel carries
 * its own course choice and names the course it is about to act on.
 *
 * WRITES GO THROUGH THE TENANT-SCOPED WRAPPER, NOT THE PLATFORM ENDPOINT — changed here after
 * measuring what the platform endpoint actually allows. `/api/instructor/v2/…/enrollments/modify`
 * is gated by `HasAccessRule('staff')`, which any GlobalStaff user satisfies for EVERY course on
 * the deployment, and every thinlms tenant admin is `is_staff=True`. Measured against the running
 * LMS: tenant A's admin got a `200` writing to tenant B's course, and a `200` reading tenant B's
 * enrolled learners. The wrapper at `/api/pathways/v1/courses/{id}/learners` delegates to the
 * platform's own `EnrollmentModifyView` — same enrolment rows, same invitation rows, same audit
 * trail — behind the org-role ∩ host-orgs rule the rest of the admin uses, and answers `404` for
 * another academy's course. The course picker below was already scoped to this academy, but that
 * was a convention in the browser, not a boundary; now it is both.
 *
 * THE ACADEMY-WIDE TABLE NOW READS A SERVER-ENFORCED BOUNDARY TOO, which the per-course roster
 * below already had: `academy/enrolments` scopes by `writable_orgs` on the server, unlike the
 * platform's own `/api/enrollment/v1/enrollments`, whose `course_ids` was merely a filter this app
 * chose to send. The roster remains the only surface that shows a pending invitation, because the
 * academy-wide endpoint answers enrolments, not invitations that have not been taken up yet.
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Banner,
  DataTable,
  Kpi,
  KpiRow,
  Pill,
  SearchField,
  Spinner,
  Tabs,
  Toolbar,
  type Column,
} from '../../shared/ui';
import { listAcademyEnrolments, type AcademyCourse, type AcademyEnrolment } from '../../shared/lib/academy';
import {
  addCourseLearner,
  bulkAddCourseLearners,
  listCourseLearners,
  removeCourseLearner,
} from '../../shared/lib/pathways-admin';
import { LearnerRoster } from '../enrolments/LearnerRoster';
import { PageHeader } from '../AdminShell';
import { CourseSwitcher, courseChoices, useCourseParam } from '../CourseFilter';
import { useAcademyCourses } from '../useAcademyCourses';

type Status = 'active' | 'removed' | 'all';

export default function AdminEnrolments() {
  // In the query string, like the other three screens: "enrolments for this course" should be a
  // link somebody can send, and it also survives a reload.
  const [course, setCourse] = useCourseParam();
  const [track, setTrack] = useState('');
  const [status, setStatus] = useState<Status>('active');
  const [search, setSearch] = useState('');

  const courses = useAcademyCourses();

  // One request for the whole academy, not scoped to `course` — the picker below is a client-side
  // filter over this same list, same as track/status/search, so changing it never refetches.
  const enrolments = useQuery({
    queryKey: ['academy-enrolments'],
    queryFn: () => listAcademyEnrolments({ pageSize: 200 }),
    staleTime: 60_000,
  });

  /** Course records by key, so the table shows a number and run alongside the row's own title. */
  const byKey = useMemo(() => {
    const map = new Map<string, AcademyCourse>();
    for (const entry of courses.all) map.set(entry.course_key, entry);
    return map;
  }, [courses.all]);

  const all = enrolments.data?.results ?? [];
  const active = useMemo(() => all.filter((row) => row.is_active), [all]);

  /** Only the tracks actually in use, so the filter offers real choices rather than a fixed list. */
  const tracks = useMemo(() => [...new Set(all.map((row) => row.mode))].sort(), [all]);

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all
      .filter((row) => (status === 'all' ? true : status === 'active' ? row.is_active : !row.is_active))
      .filter((row) => (course ? row.course_id === course : true))
      .filter((row) => (track ? row.mode === track : true))
      .filter((row) =>
        term
          ? row.user.toLowerCase().includes(term) || row.course_title.toLowerCase().includes(term)
          : true,
      )
      // Newest first: "who joined recently" is the question this page is opened with.
      .sort((a, b) => (b.created ?? '').localeCompare(a.created ?? ''));
  }, [all, status, course, track, search, byKey]);

  const auditing = active.filter((row) => row.mode === 'audit').length;

  /**
   * The switcher's list: a course, its code, and how many learners are in it.
   *
   * The count is the point. A dozen courses with similar names are told apart by their size far
   * faster than by reading their titles, and it answers "which of these is the busy one" without
   * choosing anything. Courses nobody is in are dropped by returning null, except one named in the
   * URL — a sent link must not become unselectable because its last learner left.
   */
  const choices = useMemo(() => {
    const tally = new Map<string, number>();
    for (const row of active) tally.set(row.course_id, (tally.get(row.course_id) ?? 0) + 1);
    return courseChoices(
      courses.all,
      (entry) => {
        const count = tally.get(entry.course_key) ?? 0;
        return count === 0 ? null : `${count} ${count === 1 ? 'learner' : 'learners'}`;
      },
      course,
    );
  }, [courses.all, active, course]);

  const columns: Column<AcademyEnrolment>[] = [
    { key: 'learner', header: 'Learner', render: (row) => row.user, mono: true },
    {
      key: 'course',
      header: 'Course',
      // The row already carries its own title — the map is only consulted for the number and
      // run, which the enrolment row itself does not have.
      render: (row) => {
        const found = byKey.get(row.course_id);
        return (
          <>
            {row.course_title || row.course_id}
            {found && (
              <small className="admin-sub">
                {found.number} · {found.run}
              </small>
            )}
          </>
        );
      },
    },
    {
      key: 'track',
      header: 'Track',
      render: (row) => (
        // Audit is the one track that cannot carry a certificate, so it is marked rather than
        // merely printed — an academy expecting everyone to be certified needs to see it.
        <Pill tone={row.mode === 'audit' ? 'suspended' : 'active'}>{row.mode}</Pill>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (row.is_active ? <Pill tone="active">Enrolled</Pill> : <Pill tone="retired">Left</Pill>),
    },
    {
      key: 'created',
      header: 'Since',
      render: (row) =>
        row.created ? (
          new Date(row.created).toLocaleDateString(undefined, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })
        ) : (
          <span className="admin-muted">—</span>
        ),
    },
  ];

  const loading = courses.isPending || enrolments.isPending;

  return (
    <>
      <PageHeader
        title="Enrolments"
        description="Everyone enrolled across this academy's courses, and how to add or remove them."
      />

      <div className="admin-stack">
        <KpiRow>
          <Kpi label="Enrolled now" value={loading ? '—' : active.length} />
          <Kpi
            label="Courses with learners"
            value={loading ? '—' : new Set(active.map((row) => row.course_id)).size}
            hint={courses.all.length ? `of ${courses.all.length} courses` : undefined}
          />
          <Kpi
            label="Auditing"
            value={loading ? '—' : auditing}
            hint={auditing > 0 ? 'cannot earn a certificate' : undefined}
          />
          <Kpi label="Left" value={loading ? '—' : all.length - active.length} />
        </KpiRow>

        <section className="admin-panel">
          <Toolbar>
            <Tabs
              label="Enrolment status"
              active={status}
              onChange={(id) => setStatus(id as Status)}
              tabs={[
                { id: 'active', label: 'Enrolled', count: loading ? undefined : active.length },
                { id: 'removed', label: 'Left', count: loading ? undefined : all.length - active.length },
                { id: 'all', label: 'Both', count: loading ? undefined : all.length },
              ]}
            />

            {/* Only courses with people in them: the rest would filter the table to nothing. */}
            <CourseSwitcher
              choices={choices}
              value={course}
              onChange={setCourse}
              allMeasure={`${choices.length} ${choices.length === 1 ? 'course' : 'courses'} with learners`}
              hiddenNote={
                courses.all.length > choices.length
                  ? `${courses.all.length - choices.length} of this academy's ${courses.all.length} courses have no learners and are not listed.`
                  : undefined
              }
              disabled={courses.isPending}
            />

            {tracks.length > 1 && (
              <select
                className="admin__select"
                value={track}
                onChange={(event) => setTrack(event.target.value)}
                aria-label="Filter by track"
              >
                <option value="">Any track</option>
                {tracks.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            )}

            <div className="toolbar__end">
              <SearchField value={search} onChange={setSearch} placeholder="Search learner or course" />
            </div>
          </Toolbar>

          {(courses.isError || enrolments.isError) && (
            <Banner tone="danger">
              <strong>We could not load the enrolments.</strong> Nothing has changed. Reload the
              page to try again.
            </Banner>
          )}

          {loading ? (
            <Spinner label="Loading enrolments…" />
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(row) => `${row.user}-${row.course_id}`}
              empty={
                all.length === 0
                  ? 'Nobody is enrolled in this academy yet.'
                  : 'No enrolment matches these filters.'
              }
            />
          )}

          {/*
            Say when the list is a page rather than the whole truth. The endpoint reports `count`
            precisely so this can be said outright — silently showing part of it as if it were all
            of it is how someone concludes a learner is not enrolled.
          */}
          {enrolments.data && enrolments.data.count > enrolments.data.results.length && (
            <Banner tone="info">
              <strong>
                Showing {enrolments.data.results.length} of {enrolments.data.count} enrolments.
              </strong>{' '}
              Filter by course to see everything for one, or generate an enrolment report from
              Grades for the complete list.
            </Banner>
          )}
        </section>

        {/* The roster offers EVERY course, not only the ones with people in them: enrolling the
            first person into an empty course is exactly when you need it. */}
        <CourseRoster courses={courses.all} preselected={course} onChanged={() => enrolments.refetch()} />
      </div>
    </>
  );
}

/**
 * One course's roster — enrolled, pending invites, and the controls to change both.
 *
 * A SECOND PANEL RATHER THAN A COLUMN ON THE TABLE ABOVE, because the two answer different
 * questions from different sources. The table is the whole academy at a glance, read from
 * `academy/enrolments`; this is one course in full, read from the tenant-scoped wrapper, and it
 * is the only surface that shows an invitation that has not been taken up — the academy-wide
 * endpoint returns enrolments only, so a person invited last week is simply absent from it.
 *
 * The course choice follows the filter above, which is the helpful default — somebody who has just
 * narrowed to a course is usually about to act on it — while staying overridable, because "look at
 * A, change B" is a real thing to want.
 */
function CourseRoster({
  courses,
  preselected,
  onChanged,
}: {
  courses: AcademyCourse[];
  preselected: string;
  onChanged: () => void;
}) {
  const [override, setOverride] = useState('');
  const chosen = override || preselected;
  const courseName = courses.find((entry) => entry.course_key === chosen)?.display_name;

  return (
    <>
      <section className="admin-panel">
        <header className="admin-panel__head">
          <h2>Add or remove people</h2>
        </header>

        <label className="admin-field">
          <span>Course</span>
          <select
            className="admin__select"
            value={chosen}
            onChange={(event) => setOverride(event.target.value)}
          >
            <option value="">Choose a course…</option>
            {courses.map((entry) => (
              <option key={entry.course_key} value={entry.course_key}>
                {entry.display_name} · {entry.number} {entry.run}
              </option>
            ))}
          </select>
        </label>

        {!chosen && (
          <Banner tone="info">
            <strong>Choose a course to see who is on it.</strong> An enrolment belongs to a course,
            so there is nowhere to add anybody until one is picked.
          </Banner>
        )}
      </section>

      {/* KEYED ON THE COURSE so that switching courses remounts the roster. Without it the
          previous course's typed-but-unsent email list would survive the change and be one click
          away from being applied to the wrong course. */}
      {chosen && (
        <CourseRosterPanel key={chosen} courseKey={chosen} name={courseName} onChanged={onChanged} />
      )}
    </>
  );
}

function CourseRosterPanel({
  courseKey,
  name,
  onChanged,
}: {
  courseKey: string;
  name: string | undefined;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();

  /**
   * After any change, refresh the academy-wide table too.
   *
   * It is a different query against a different endpoint, so it does not invalidate itself when
   * this one does — and a table that still shows the person you just removed reads as a failed
   * removal.
   */
  const alsoRefresh = <T,>(result: T): T => {
    onChanged();
    queryClient.invalidateQueries({ queryKey: ['academy-enrolments'] });
    return result;
  };

  return (
    <>
      <LearnerRoster
        subject="course"
        showTrack
        // The server decides, and it answers 404 for another academy's course. Nothing is
        // pre-empted here: this page is only reachable by a tenant admin, and the write is
        // re-checked against the course's own org on every request.
        writable
        api={{
          queryKey: ['course-learners', courseKey],
          list: () => listCourseLearners(courseKey),
          add: (who, notify) =>
            addCourseLearner(courseKey, { ...who, email_students: notify }).then(alsoRefresh),
          addMany: (emails, notify) =>
            bulkAddCourseLearners(courseKey, emails, { email_students: notify }).then(alsoRefresh),
          remove: (identifier) => removeCourseLearner(courseKey, identifier).then(alsoRefresh),
        }}
        notify={{
          label: 'Email them',
          hint: "The academy's own enrolment email. Bounces are not reported back.",
        }}
        inviteNote={
          <Banner tone="info">
            <strong>An address with no account yet becomes a pending invite.</strong> It turns into
            a real enrolment the moment that person registers with that exact address. Unless you
            tick “Email them” below, the platform sends nothing — so you have to tell them
            yourself. Cancelling a pending invite deletes it outright and leaves no record, unlike
            removing somebody who was already enrolled.
            {name ? <> Changes apply to <strong>{name}</strong>.</> : null}
          </Banner>
        }
      />
    </>
  );
}

